import React from 'react';
import { motion } from 'framer-motion';

// Image and video imports
const tmobileVideo = '/video/t-mobile-5gin-video.mp4';
const tmobileMobile1 = '/images/t-mobile-5gin-mobile-1.png';
const tmobileMobile2 = '/images/t-mobile-5gin-mobile-2.png';
const tmobileMobile3 = '/images/t-mobile-5gin-mobile-3.png';
const tmobileDesktop3 = '/images/t-mobile-5gin-desktop-3.png';
const tmobileDesktop2 = '/images/t-mobile-5gin-desktop-2.png';
const tmobileDesktop4 = '/images/t-mobile-5gin-desktop-4.png';
const tmobileARDesktop = '/images/t-mobile-5gin-AR-experience-desktop.png';
const tmobileARMobile = '/images/t-mobile-5gin-AR-experience-mobile.png';

const TMobile5Gin: React.FC = () => {
  return (
    <div className="flex flex-1 flex-col gap-2">
      <div className="mx-auto max-w-8xl flex flex-1 flex-col gap-2 p-4 md:p-6">
        <section className="grid md:grid-cols-12 my-4 md:my-8">
          <div className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-6 md:space-y-12">
            <motion.h1
              className="md:!leading-tight text-3xl md:text-4xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{
                duration: 0.6,
                delay: 0.1,
                ease: "easeOut",
              }}
            >
              Designed and coded T-Mobile 5Gin's landing page and AR experience to drive user awareness.
            </motion.h1>
            <motion.div
              className="grid md:grid-cols-12 md:gap-6 space-y-4 md:space-y-0 col-span-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{
                duration: 0.6,
                delay: 0.2,
                ease: "easeOut",
              }}
            >
              <div className="md:col-span-5 space-y-4 md:space-y-8">
                <div className="space-y-1 md:space-y-3">
                  <p className="font-mono-recursive md:text-lg">Team</p>
                  <p className="md:text-xl dark:dark:text-zinc-400">1x Product Manager<span className="mx-2">·</span>1x Graphic Designer</p>
                </div>
                <div className="space-y-1 md:space-y-3">
                  <p className="font-mono-recursive md:text-lg">Tools & Frameworks</p>
                  <p className="md:text-xl dark:dark:text-zinc-400">Figma<span className="mx-1"> · </span>TailwindCSS</p>
                </div>
                <div className="space-y-1 md:space-y-3">
                  <p className="font-mono-recursive md:text-lg">Date</p>
                  <p className="md:text-xl dark:dark:text-zinc-400">2021</p>
                </div>
              </div>
              <div className="md:col-span-7 space-y-4 md:space-y-8">
                <div className="space-y-1 md:space-y-3">
                  <p className="font-mono-recursive md:text-lg">Role & Contributions</p>
                  <p className="md:text-xl dark:dark:text-zinc-400">Product Designer<span className="mx-2">·</span>Prototyping<span className="mx-2">·</span>Front-end Development</p>
                </div>
                <div className="space-y-1 md:space-y-3">
                  <p className="font-mono-recursive md:text-lg">Key Achievements</p>
                  <p className="md:text-xl dark:dark:text-zinc-400">Designed and shipped the landing page and AR experience for T-Mobile's 5Gin campaign. The limited-edition gin sold out in minutes, with awareness driven by the website and interactive AR experience.</p>
                </div>
                <div className="inline-flex mt-4">
                  <a 
                    href="https://www.t-mobile.com/news/network/ultra-capacity-5gin-extended-range-5ginger-beer"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono-recursive md:text-lg transition text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline underline-offset-4 cursor-pointer"
                  >
                    Read News Article
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="mt-8 md:mt-14">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <video 
              className="z-10 overflow-hidden rounded-lg md:rounded-2xl relative w-full" 
              autoPlay 
              loop 
              muted 
              playsInline
            >
              <source src={tmobileVideo} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <figcaption className="text-center italic pt-2 text-gray-500 font-mono-recursive">
              T-Mobile 5Gin Marketing Site
            </figcaption>
          </motion.div>

          <div className="grid md:grid-cols-12 my-12 md:my-28 space-y-12 md:space-y-28">
            <motion.div
              className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <h2 className="text-xl md:text-2xl font-medium">Project Overview</h2>
              <p className="lg:leading-normal text-md md:text-xl dark:text-zinc-400">To celebrate their 5G network, T-Mobile created a special limited run of spirits called 5Gin. T-Mobile Contracted Porter Novelli who I regularly freelancer for and was given the task to design and assist in the front-end development of a landing page and AR experience page. Using T-Mobile's strict design guide, I made use of their big bold typography and bold colors.</p>
            </motion.div>

            <motion.div
              className="col-span-12 grid md:grid-cols-3 gap-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
                viewport={{ once: true, amount: 0.3 }}
              >
                <img className="rounded-lg md:rounded-2xl" src={tmobileMobile1} alt="T-Mobile 5Gin mobile view 1" />
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
                viewport={{ once: true, amount: 0.3 }}
              >
                <img className="rounded-lg md:rounded-2xl" src={tmobileMobile2} alt="T-Mobile 5Gin mobile view 2" />
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, ease: "easeOut", delay: 0.3 }}
                viewport={{ once: true, amount: 0.3 }}
              >
                <img className="rounded-lg md:rounded-2xl" src={tmobileMobile3} alt="T-Mobile 5Gin mobile view 3" />
              </motion.div>
            </motion.div>

            <motion.div
              className="col-span-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.4 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <img className="rounded-lg md:rounded-2xl" src={tmobileDesktop3} alt="T-Mobile 5Gin desktop view 3" />
            </motion.div>

            <motion.div
              className="col-span-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.5 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <img className="rounded-lg md:rounded-2xl" src={tmobileDesktop2} alt="T-Mobile 5Gin desktop view 2" />
            </motion.div>

            <motion.div
              className="col-span-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.6 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <img className="rounded-lg md:rounded-2xl" src={tmobileDesktop4} alt="T-Mobile 5Gin desktop view 4" />
            </motion.div>

            <motion.div
              className="col-span-12 grid md:grid-cols-3 gap-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.7 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <motion.div
                className="col-span-2"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
                viewport={{ once: true, amount: 0.3 }}
              >
                <img className="rounded-lg md:rounded-2xl" src={tmobileARDesktop} alt="T-Mobile 5Gin AR experience desktop" />
              </motion.div>
              <motion.div
                className="col-span-1"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
                viewport={{ once: true, amount: 0.3 }}
              >
                <img className="rounded-lg md:rounded-2xl" src={tmobileARMobile} alt="T-Mobile 5Gin AR experience mobile" />
              </motion.div>
            </motion.div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default TMobile5Gin;
